import {
  Banner,
  Box,
  Button,
  Chip,
  Grid,
  GridColumn,
  Text,
  ThemeProvider
} from "@modernatx/ui-kit-react";
import { AxiosError } from "axios";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useHistory, useLocation } from "react-router-dom";

import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { PaymentForm } from "@/components/PaymentForm";
import { PaymentMethodCard } from "@/components/PaymentMethodCard";
import { PaymentSummaryCard } from "@/components/PaymentSummaryCard";
import { useFeatureFlag } from "@/context/FeatureFlagContext";
import { useUserInfoContext } from "@/contexts";
import { useInvoiceDetail } from "@/hooks/useInvoiceDetail";
import { useSavedPaymentMethods } from "@/hooks/useSavedPaymentMethod";
import { Invoice } from "@/types/Invoice";
import { PaymentMethod } from "@/types/PaymentMethod";
import {
  formatCurrency,
  formatTimestampDate,
  generateIdempotencyToken
} from "@/utils/InvoicePay";

import bannerStrand from "../assets/images/banner-strand-top.png";
import { DEFAULT_CURRENCY } from "../constants";
import { getCapturePaymentErrorMessages } from "../utils/getErrorMessage";
import { capturePayment } from "../utils/PaymentsService/PaymentMethodApis";

const PaymentPage = () => {
  const { allPaymentMethods, defaultPaymentMethod } = useSavedPaymentMethods();
  const isMultiInvoicePayAvailable = useFeatureFlag("MULTI_INVOICE_PAY");

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  };

  const query = useQuery();
  const invoiceNo = query.get("invoice") || "";

  const { fetchInvoice, setIsLoading, isLoading } = useInvoiceDetail();
  const [invoice, setInvoiceData] = useState<Invoice | null>(null);

  const paymentFormSubmitRef =
    useRef<() => Promise<PaymentMethod | undefined>>();

  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isPaymentsLoading, setIsPaymentsLoading] = useState(false);
  const [isPaymentMethodSelected, setIsPaymentMethodSelected] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PaymentMethod | undefined
  >();
  const { userInfo } = useUserInfoContext() as {
    userInfo: { userId: string };
  };

  useEffect(() => {
    if (defaultPaymentMethod) {
      setIsPaymentMethodSelected(true);
      setSelectedPaymentMethod(defaultPaymentMethod);
    }
  }, [defaultPaymentMethod]);

  const onDismissBanner = useCallback(() => {
    setPaymentError(null);
  }, []);

  useEffect(() => {
    const fetchInvoiceDetail = async () => {
      setIsLoading(true);
      const invoiceDetail = await fetchInvoice(invoiceNo);

      if (invoiceDetail) {
        setInvoiceData(invoiceDetail);
        setIsLoading(false);
      }
    };
    fetchInvoiceDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceNo]);

  const invoicePriceSummary = useMemo(() => {
    const subtotal = invoice?.subtotal ?? 0;
    const tax = invoice?.taxes ?? 0;
    const total = invoice?.total ?? 0;
    const discount = invoice?.discounts?.[0]?.discountAmount ?? 0;
    const promptPayDiscountedTotal = total - discount;
    const totalDoses = invoice?.orderLineItems?.reduce(
      (accumulator, currentValue) =>
        accumulator + Number(currentValue.quantity),
      0
    );

    const promptPayDueDate = invoice?.dueDate;

    return {
      amountDue: total,
      totalDoses,
      subtotal,
      tax,
      netInvoice: subtotal + tax,
      promptPayDiscount: discount,
      promptPayDiscountedTotal,
      promptPayDueDate
    };
  }, [invoice]);

  const history = useHistory();

  const handlePayNow = async () => {
    const getPaymentMethodId = async (): Promise<string | undefined> => {
      if (selectedPaymentMethod) {
        return selectedPaymentMethod.paymentMethodId;
      } else {
        const newPaymentMethod = await paymentFormSubmitRef.current?.();
        if (newPaymentMethod) {
          setSelectedPaymentMethod(newPaymentMethod);
          return newPaymentMethod.paymentMethodId;
        }
      }
    };

    const submitPayment = async (paymentMethodId: string) => {
      const idempotencyToken = generateIdempotencyToken();
      const capturePayload = {
        idempotencyToken,
        invoiceId: invoice?.invoiceNo,
        orderId: invoice?.invoiceNo,
        amount: invoice?.total,
        paymentMethodId,
        metadata: {
          capOrgId: invoice?.billTo.id,
          upsCustomerId: "TBD",
          soldToCapId: invoice?.soldTo.id,
          billToCapId: invoice?.billTo.id,
          shipToCapId: invoice?.shipTo.id,
          soldToOrgName: invoice?.soldTo.name,
          orderId: invoice?.orderNumber,
          orderNumber: invoice?.orderNumber,
          userId: userInfo?.userId,
          paymentType: "INVOICE_PAY",
          invoiceId: invoice?.invoiceNo
        },
        currency: DEFAULT_CURRENCY
      };

      try {
        setIsPaymentsLoading(true);
        const response = await capturePayment(capturePayload);
        if (response.status === 200) {
          const paymentId = response.data.paymentDetails.paymentId;
          history.push(`/payment/confirmation?paymentId=${paymentId}`);
        }
      } catch (err: unknown) {
        if (err instanceof AxiosError && err.response?.data?.errorCode) {
          const message = getCapturePaymentErrorMessages(
            err.response.data.errorCode
          );
          setPaymentError(message);
        }
      } finally {
        setIsPaymentsLoading(false);
      }
    };

    const paymentMethodId = await getPaymentMethodId();
    if (paymentMethodId) {
      await submitPayment(paymentMethodId);
    }
  };

  const showLoadingSpinner = isLoading || isPaymentsLoading;

  return (
    <ThemeProvider>
      {showLoadingSpinner ? (
        <LoadingSpinner
          size="xl"
          message={
            isPaymentsLoading
              ? "We’re processing your payment. Please do not refresh or close this page."
              : undefined
          }
        />
      ) : (
        <>
          <Box
            sx={{
              backgroundImage: `url(${bannerStrand})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "contain",
              backgroundPosition: "center right",
              backgroundColor: "onSurface-brand-primary-hover",
              width: "100%"
            }}
          >
            <Grid>
              <GridColumn>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    py: "spacing-8",
                    alignItems: "flex-start",
                    gap: "spacing-4"
                  }}
                >
                  <Text variant="h4" sx={{ color: "white" }}>
                    Make a payment
                  </Text>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "spacing-3",
                      alignItems: "center"
                    }}
                  >
                    <Text variant="h6" sx={{ color: "white" }}>
                      Invoice:{" "}
                    </Text>
                    <Chip
                      size="large"
                      type="info"
                      label={`#${invoice?.invoiceNo}`}
                      // TO-DO: MD-278 on dismiss should show invoice table
                      // onDismiss={() => {}}
                    />
                  </Box>
                  {isMultiInvoicePayAvailable && (
                    <Button
                      aria-label="Select invoices"
                      icon="caret-down"
                      variant="subtle"
                      iconPosition="end"
                      size="large"
                      sx={{ color: "white", padding: "0" }}
                      onClick={() => alert("Invoice selection")}
                    >
                      Select invoices
                    </Button>
                  )}
                </Box>
              </GridColumn>
            </Grid>
          </Box>
          {paymentError && (
            <Banner
              variant="critical"
              title="Error while processing payment"
              text={<Text>{paymentError}</Text>}
              onDismiss={onDismissBanner}
            />
          )}
          <Grid sx={{ gap: "spacing-8", width: "100%", py: "spacing-8" }}>
            <GridColumn size={[3, 5, 6]}>
              <Text sx={{ textDecoration: "line-through" }}>
                {formatCurrency(
                  (invoice?.subtotal ?? 0) + (invoice?.taxes ?? 0)
                )}
              </Text>
              <Text variant="h3">{formatCurrency(invoice?.total)}*</Text>
              <Text variant="body-1-regular">
                Amount if paid by
                <Text sx={{ mx: "spacing-2" }}>
                  {formatTimestampDate(invoice?.dueDate ?? "--")}
                </Text>
                *
              </Text>
              <Box sx={{ pt: "spacing-4" }}>
                {defaultPaymentMethod ? (
                  <PaymentMethodCard
                    defaultPaymentMethod={defaultPaymentMethod}
                    allPaymentMethods={allPaymentMethods}
                    selectedPaymentMethod={selectedPaymentMethod}
                    setSelectedPaymentMethod={setSelectedPaymentMethod}
                  />
                ) : (
                  <PaymentForm
                    setSubmitRef={(fn) => {
                      paymentFormSubmitRef.current = fn;
                    }}
                  />
                )}
              </Box>
            </GridColumn>
            <GridColumn size={[3, 5, 5]}>
              <PaymentSummaryCard
                invoicePriceSummary={invoicePriceSummary}
                onPayNow={handlePayNow}
                isPaymentMethodSelected={isPaymentMethodSelected}
                isPaymentFormOpen={!defaultPaymentMethod}
              />
            </GridColumn>
          </Grid>
        </>
      )}
    </ThemeProvider>
  );
};

export default PaymentPage;
